import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  message,
  Tooltip,
  Alert,
} from 'antd';
import {
  LoginOutlined,
  ReloadOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { loginLogsAPI } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const FAIL_REASON_LABEL = {
  no_such_account: '账号不存在',
  wrong_password: '密码错误',
  deactivated: '账号已停用',
};

/** UA 里挑出「浏览器 / 系统」这种一眼能看懂的信息，完整 UA 放 tooltip */
const summarizeUserAgent = (ua) => {
  const s = String(ua || '');
  if (!s) return '—';

  let os = '未知系统';
  if (/Windows NT 10/.test(s)) os = 'Windows 10/11';
  else if (/Windows NT/.test(s)) os = 'Windows';
  else if (/iPhone|iPad/.test(s)) os = 'iOS';
  else if (/Android/.test(s)) os = 'Android';
  else if (/Mac OS X/.test(s)) os = 'macOS';
  else if (/Linux/.test(s)) os = 'Linux';

  let browser = '未知浏览器';
  if (/Edg\//.test(s)) browser = 'Edge';
  else if (/OPR\//.test(s)) browser = 'Opera';
  else if (/Chrome\//.test(s)) browser = 'Chrome';
  else if (/Safari\//.test(s)) browser = 'Safari';
  else if (/Firefox\//.test(s)) browser = 'Firefox';

  return `${browser} / ${os}`;
};

const LoginLogs = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  // 已提交的筛选条件（改输入框不会立刻打接口，点查询才生效）
  const [filters, setFilters] = useState({ keyword: '', status: 'all', range: null });
  const [draft, setDraft] = useState({ keyword: '', status: 'all', range: null });

  const fetchData = useCallback(async (page, activeFilters) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (activeFilters.keyword) params.keyword = activeFilters.keyword;
      if (activeFilters.status !== 'all') params.status = activeFilters.status;
      if (activeFilters.range && activeFilters.range.length === 2) {
        params.startDate = activeFilters.range[0].startOf('day').toISOString();
        params.endDate = activeFilters.range[1].endOf('day').toISOString();
      }

      const [logsRes, statsRes] = await Promise.all([
        loginLogsAPI.getAll(params),
        loginLogsAPI.getStats(),
      ]);

      if (logsRes.data.success) {
        setLogs(logsRes.data.data.logs || []);
        setPagination((prev) => ({
          ...prev,
          page,
          total: logsRes.data.data.pagination.total,
        }));
      }
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
    } catch (error) {
      message.error('获取登录记录失败');
      console.error('Fetch login logs error:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchData(1, filters);
  }, [fetchData, filters]);

  const handleSearch = () => {
    setFilters(draft);
  };

  const handleReset = () => {
    const empty = { keyword: '', status: 'all', range: null };
    setDraft(empty);
    setFilters(empty);
  };

  const columns = [
    {
      title: '登录时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
    },
    {
      title: '账号',
      width: 200,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.username || r.credential || '—'}</Text>
          {r.email ? (
            <Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.userId ? '—' : '（账号不存在）'}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '身份',
      width: 100,
      render: (_, r) => {
        if (!r.role) return <Text type="secondary">—</Text>;
        if (r.role === 'admin') return <Tag color="red">管理员</Tag>;
        if (r.role === 'vip') return <Tag color="gold">VIP</Tag>;
        return <Tag>普通用户</Tag>;
      },
    },
    {
      title: 'IP 地址',
      dataIndex: 'ip',
      width: 150,
      render: (v) => (v ? <Text copyable style={{ fontFamily: 'monospace' }}>{v}</Text> : '—'),
    },
    {
      title: '设备',
      dataIndex: 'userAgent',
      width: 180,
      render: (v) => (
        <Tooltip title={v || '无 UA'}>
          <Text type="secondary">{summarizeUserAgent(v)}</Text>
        </Tooltip>
      ),
    },
    {
      title: '结果',
      dataIndex: 'status',
      width: 130,
      render: (v, r) =>
        v === 'success' ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>成功</Tag>
        ) : (
          <Tooltip title={FAIL_REASON_LABEL[r.failReason] || r.failReason || '未知原因'}>
            <Tag color="red" icon={<CloseCircleOutlined />}>
              {FAIL_REASON_LABEL[r.failReason] || '失败'}
            </Tag>
          </Tooltip>
        ),
    },
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="客户登录记录"
        description="记录每次登录的账号、IP、设备与结果（含失败尝试）。功能上线后产生的登录才会入库，历史登录无法追溯。"
      />

      <Row gutter={16}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="今日登录成功" value={stats.todaySuccess || 0} prefix={<LoginOutlined />} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="今日活跃客户" value={stats.todayUsers || 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="今日独立 IP" value={stats.todayIps || 0} prefix={<GlobalOutlined />} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="累计失败尝试"
              value={stats.failed || 0}
              valueStyle={{ color: (stats.failed || 0) > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={<Title level={5} style={{ margin: 0 }}>登录记录</Title>}
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => fetchData(pagination.page, filters)}>
            刷新
          </Button>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索用户名 / 邮箱 / IP"
            allowClear
            style={{ width: 240 }}
            value={draft.keyword}
            onChange={(e) => setDraft((d) => ({ ...d, keyword: e.target.value }))}
            onPressEnter={handleSearch}
          />
          <Select
            style={{ width: 130 }}
            value={draft.status}
            onChange={(v) => setDraft((d) => ({ ...d, status: v }))}
          >
            <Option value="all">全部结果</Option>
            <Option value="success">仅成功</Option>
            <Option value="failed">仅失败</Option>
          </Select>
          <RangePicker
            value={draft.range}
            onChange={(v) => setDraft((d) => ({ ...d, range: v }))}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={logs}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => fetchData(p, filters),
          }}
        />
      </Card>
    </Space>
  );
};

export default LoginLogs;
